<template>
  <div class="labs-extrusions">
    <div class="labs-section-header">
      <h3>Extrusions</h3>
      <div class="labs-header-actions">
        <input
          type="text"
          v-model="searchText"
          placeholder="Search extrusions..."
          class="labs-search-input"
        />
      </div>
    </div>

    <div v-if="loading" class="labs-loading">Loading extrusions...</div>

    <div v-else-if="filteredExtrusions.length === 0" class="labs-empty">
      No extrusions found.
    </div>

    <div v-else class="labs-table-wrapper">
      <table class="labs-table">
        <thead>
          <tr>
            <th class="col-visible">Visible</th>
            <th class="col-test">Test #</th>
            <th class="col-type">Type</th>
            <th class="col-extruder">Extruder</th>
            <th class="col-date">Date</th>
            <th class="col-die">Die #</th>
            <th class="col-rpm">RPM</th>
            <th class="col-pressure">Pressure (bar)</th>
            <th class="col-throughput">Throughput</th>
            <th class="col-wbm">WBM %</th>
            <th class="col-success">Successful</th>
            <th class="col-rating">Rating</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="ext in paginatedExtrusions"
            :key="ext.id"
            :class="{ 'row-hidden': !ext.visibleToCustomer }"
          >
            <td class="col-visible">
              <label class="labs-toggle" v-if="canToggleVisibility">
                <input
                  type="checkbox"
                  :checked="ext.visibleToCustomer"
                  @change="toggleVisibility(ext)"
                />
                <span class="labs-toggle-slider"></span>
              </label>
              <span v-else class="labs-visibility-badge" :class="ext.visibleToCustomer ? 'visible' : 'hidden'">
                {{ ext.visibleToCustomer ? 'Yes' : 'No' }}
              </span>
            </td>
            <td class="col-test">{{ ext.testNumber || '—' }}</td>
            <td class="col-type">{{ ext.extrusionType || '—' }}</td>
            <td class="col-extruder">{{ ext.extruderType || '—' }}</td>
            <td class="col-date">{{ formatDate(ext.extrusionDate) }}</td>
            <td class="col-die">{{ ext.dieNumber || '—' }}</td>
            <td class="col-rpm">{{ formatDecimal(ext.extruderRpm) }}</td>
            <td class="col-pressure">{{ formatDecimal(ext.extrusionPressureBar) }}</td>
            <td class="col-throughput">
              {{ formatDecimal(ext.throughputGMin) }}
              <span v-if="ext.throughputUnit" class="unit-label">{{ ext.throughputUnit }}</span>
            </td>
            <td class="col-wbm">{{ formatDecimal(ext.wbm) }}</td>
            <td class="col-success">
              <span
                v-if="ext.successful"
                class="labs-success-badge"
                :class="ext.successful.toLowerCase()"
              >
                {{ ext.successful }}
              </span>
              <span v-else>—</span>
            </td>
            <td class="col-rating">
              {{ ext.extrudabilityRating0To10 != null ? ext.extrudabilityRating0To10 + '/10' : '—' }}
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="labs-pagination" v-if="totalPages > 1">
        <button @click="currentPage = 1" :disabled="currentPage === 1">&laquo;</button>
        <button @click="currentPage--" :disabled="currentPage === 1">&lsaquo;</button>
        <span class="labs-page-info">Page {{ currentPage }} of {{ totalPages }}</span>
        <button @click="currentPage++" :disabled="currentPage === totalPages">&rsaquo;</button>
        <button @click="currentPage = totalPages" :disabled="currentPage === totalPages">&raquo;</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Extrusions',
  props: {
    labsCustomerId: { type: Number, required: true },
    apiBase: { type: String, required: true },
    canToggleVisibility: { type: Boolean, default: false }
  },
  data() {
    return {
      extrusions: [],
      searchText: '',
      loading: false,
      currentPage: 1,
      pageSize: 20
    };
  },
  computed: {
    filteredExtrusions() {
      if (!this.searchText.trim()) return this.extrusions;
      const q = this.searchText.toLowerCase();
      return this.extrusions.filter(e =>
        (e.testNumber && e.testNumber.toLowerCase().includes(q)) ||
        (e.extrusionType && e.extrusionType.toLowerCase().includes(q)) ||
        (e.extruderType && e.extruderType.toLowerCase().includes(q)) ||
        (e.dieNumber && e.dieNumber.toLowerCase().includes(q)) ||
        (e.comments && e.comments.toLowerCase().includes(q))
      );
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredExtrusions.length / this.pageSize));
    },
    paginatedExtrusions() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredExtrusions.slice(start, start + this.pageSize);
    }
  },
  watch: {
    labsCustomerId: 'loadExtrusions',
    searchText() { this.currentPage = 1; }
  },
  created() {
    this.loadExtrusions();
  },
  methods: {
    async loadExtrusions() {
      if (!this.labsCustomerId) return;
      this.loading = true;
      try {
        const resp = await axios.get(
          `${this.apiBase}/api/labs/customers/${this.labsCustomerId}/extrusions`
        );
        this.extrusions = resp.data || [];
      } catch (err) {
        console.error('Failed to load extrusions:', err);
        this.extrusions = [];
      } finally {
        this.loading = false;
      }
    },

    async toggleVisibility(ext) {
      const newVal = !ext.visibleToCustomer;
      try {
        await axios.patch(
          `${this.apiBase}/api/labs/extrusions/${ext.id}/visibility`,
          { visible: newVal }
        );
        ext.visibleToCustomer = newVal;
      } catch (err) {
        console.error('Failed to toggle extrusion visibility:', err);
      }
    },

    formatDate(val) {
      if (!val) return '—';
      return new Date(val).toLocaleDateString();
    },

    formatDecimal(val) {
      if (val == null) return '—';
      return Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
  }
};
</script>

<style>
/* Extrusions — non-scoped so styles survive builds (same pattern as Mixes.vue) */
.labs-extrusions .labs-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.labs-extrusions .labs-section-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #1a1a1a;
}
.labs-extrusions .labs-search-input {
  padding: 6px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.875rem;
  width: 220px;
}
.labs-extrusions .labs-table-wrapper {
  overflow-x: auto;
}
.labs-extrusions .labs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.labs-extrusions .labs-table th,
.labs-extrusions .labs-table td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e5e5e5;
  white-space: nowrap;
}
.labs-extrusions .labs-table thead th {
  background: #f5f6f8;
  font-weight: 600;
  color: #555;
  position: sticky;
  top: 0;
  z-index: 1;
}
.labs-extrusions .labs-table tbody tr:hover {
  background: #f9fafb;
}
.labs-extrusions .row-hidden {
  opacity: 0.55;
}
.labs-extrusions .labs-success-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
}
.labs-extrusions .labs-success-badge.yes {
  background: #d4edda;
  color: #155724;
}
.labs-extrusions .labs-success-badge.no {
  background: #f8d7da;
  color: #721c24;
}
.labs-extrusions .labs-success-badge.partial {
  background: #fff3cd;
  color: #856404;
}
.labs-extrusions .unit-label {
  color: #888;
  font-size: 0.75rem;
  margin-left: 2px;
}

/* Toggle switch */
.labs-extrusions .labs-toggle {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
}
.labs-extrusions .labs-toggle input { opacity: 0; width: 0; height: 0; }
.labs-extrusions .labs-toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 20px;
  transition: 0.2s;
}
.labs-extrusions .labs-toggle-slider::before {
  content: '';
  position: absolute;
  width: 16px;
  height: 16px;
  left: 2px;
  bottom: 2px;
  background: #fff;
  border-radius: 50%;
  transition: 0.2s;
}
.labs-extrusions .labs-toggle input:checked + .labs-toggle-slider {
  background: #28a745;
}
.labs-extrusions .labs-toggle input:checked + .labs-toggle-slider::before {
  transform: translateX(16px);
}
.labs-extrusions .labs-visibility-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
}
.labs-extrusions .labs-visibility-badge.visible { background: #d4edda; color: #155724; }
.labs-extrusions .labs-visibility-badge.hidden  { background: #e9ecef; color: #6c757d; }

/* Pagination */
.labs-extrusions .labs-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 0;
}
.labs-extrusions .labs-pagination button {
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 0.85rem;
  color: #333;
}
.labs-extrusions .labs-pagination button:disabled {
  opacity: 0.4;
  cursor: default;
}
.labs-extrusions .labs-pagination button:not(:disabled):hover {
  background: #f0f0f0;
}
.labs-extrusions .labs-page-info {
  font-size: 0.85rem;
  color: #555;
  margin: 0 8px;
}

/* Loading & empty states */
.labs-extrusions .labs-loading,
.labs-extrusions .labs-empty {
  padding: 24px;
  text-align: center;
  color: #888;
}
</style>
